/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type { UserAuth } from '@repo/common';
import { type AxiosResponse } from 'axios';
import { get } from 'svelte/store';
import { invalidate } from '../../src/lib/invalidate.util';
import { getPrivateClient } from '../data/common.data';
import { getHandler } from '../sharedlib';

import {
    authLoading,
    resetUser,
    resetUserAuth,
    setUser,
    setUserAuth,
    user,
    userAuthStore,
    userAuthToken
} from '../stores/auth.store';
import { config, getApiEndpoint } from '../stores/config';
import { userOnboardingDataLoaded } from '../stores/onboarding.store';
import { viewer } from '../stores/viewer';
import constants, { getAvatarCdnUrl } from '../utils/constants';
import { decodeJWT, extractTokenMetadata } from '../utils/jwt.util';
import { updateLayout } from './layout-utils.actions';

export const signOut = async () => {
  const $user = get(user);
  const handler = getHandler();

  if ($user.id) {
    await resetUser(true);
  }
  await resetUserAuth(true);
  await handler.signOutSession();

  authLoading.set(false);

  viewer.update(($viewer) => {
    // Sign out user
    $viewer.activeType = 'home';
    return $viewer;
  });
};

export const continueSignIn = async (urls: string[]) => {
  if (urls.length) {
    authLoading.set(true);
    const urlString = urls[0];
    console.log('Received URL String:', urlString);
    const parsedUrl = new URL(urlString);
    const queryParams = parsedUrl.searchParams;
    const idToken = queryParams.get('idToken');
    const refreshToken = queryParams.get('refreshToken');
    const receivedEndpoint = queryParams.get('endpoint')
      ? decodeURIComponent(queryParams.get('endpoint') as string)
      : null;
    const endpoint = getApiEndpoint();
    if (receivedEndpoint && receivedEndpoint != endpoint) {
      if (!constants.ALLOWED_ENDPOINTS.includes(receivedEndpoint)) {
        console.error('Invalid endpoint', receivedEndpoint);
      } else {
        updateEndpoint(receivedEndpoint);
        alert(`Changed environment to ${receivedEndpoint}`);
      }
    }
    if (idToken && refreshToken) {
      try {
        await loadSignedInUser(idToken, refreshToken);
      } catch (error) {
        console.debug('Error loading signed in user', error);
        console.error('Please sign in again');
        signOut();
      } finally {
        authLoading.set(false);
      }
    } else {
      console.error('No idToken or refreshToken found');
    }
    updateLayout(undefined);
  }
};

export const updateEndpoint = (receivedEndpoint: string) => {
  if (!constants.ALLOWED_ENDPOINTS.includes(receivedEndpoint)) {
    throw new Error('Invalid endpoint');
  }
  config.update(($config) => {
    $config.apiEndpoint = receivedEndpoint;
    return $config;
  });
};

export const loadSignedInUser = async (
  idToken: string | null,
  refreshToken: string,
  forceRefreshApiToken: boolean = false
) => {
  const client = getPrivateClient(
    { idToken: idToken, refreshToken: refreshToken },
    { timeout: constants.TIMEOUT_LONG, retries: 3 }
  );

  // Add force refresh header if needed
  if (forceRefreshApiToken) {
    client.defaults.headers['x-force-refresh-api-token'] = 'true';
  }

  let response: AxiosResponse;

  try {
    response = await client.get('/user');
  } catch (error) {
    console.error('Error loading signed in user', (error as any)?.message);
    throw error;
  }

  const { data } = response;
  const apiToken = response.headers['x-api-token'];

  // If we're only refreshing the API token, just update auth and return
  // This prevents triggering reactivity in components watching the user store
  if (forceRefreshApiToken) {
    // Get current auth to preserve existing tokens during atomic swap
    const currentAuth = get(userAuthStore);

    // Only update the tokens we received - preserve other tokens
    // This ensures atomic credential swapping with no gaps in availability
    const effectiveIdToken = idToken || currentAuth.idToken;
    let metadata = {};
    if (effectiveIdToken) {
      try {
        const decoded = decodeJWT(effectiveIdToken);
        metadata = {
          ...extractTokenMetadata(decoded.payload),
          lastValidated: Date.now()
        };
      } catch (e) {
        console.error('Failed to decode JWT for metadata', e);
      }
    }

    const updatePayload: UserAuth = {
      idToken: effectiveIdToken,
      refreshToken: refreshToken,
      apiToken: apiToken || currentAuth.apiToken,
      apiTokenM: currentAuth.apiTokenM, // Preserve existing minimodel token
      ...metadata
    };

    setUserAuth(updatePayload, true, 'loaded', true);
    return;
  }

  getHandler().setUserId(data.id);

  // Set user to loaded, so the store doesn't overwrite it.
  setUser(
    {
      ...data,
      picture: data.picture ? getAvatarCdnUrl(data.picture) : data.picture
    },
    true,
    'loaded'
  );
  
  userOnboardingDataLoaded.set(true);

  // Get current auth to preserve existing tokens during atomic swap
  const currentAuth = get(userAuthStore);
  const effectiveIdToken = idToken || currentAuth.idToken;
  let metadata = {};
  if (effectiveIdToken) {
    try {
      const decoded = decodeJWT(effectiveIdToken);
      metadata = {
        ...extractTokenMetadata(decoded.payload),
        lastValidated: Date.now()
      };
    } catch (e) {
      console.error('Failed to decode JWT for metadata', e);
    }
  }

  setUserAuth(
    {
      idToken: effectiveIdToken,
      refreshToken: refreshToken,
      apiToken: apiToken || currentAuth.apiToken,
      apiTokenM: currentAuth.apiTokenM, // Preserve existing minimodel token
      ...metadata
    },
    true,
    'loaded',
    true
  );

  // Note: invalidate() calls removed for Svelte 5 compatibility
  // The stores are reactive and will update automatically
  await invalidate('user:calendars');
  await invalidate('user:notifications');
  await invalidate('user:email');
};

export const deleteAccount = async () => {
  const $user = get(user);
  const handler = getHandler();

  if (!$user?.email) {
    throw new Error('No user email found. Please sign in first.');
  }

  try {
    console.debug('Deleting account...', $user.id);
    const client = getPrivateClient(
      { idToken: get(userAuthToken).idToken, refreshToken: get(userAuthToken).refreshToken },
      { timeout: 10000 }
    );

    // Call the delete account endpoint
    await client.delete('/user');

    console.debug('Removing sqlite database');
    // Remove the sqlite database
    await getHandler().removeSqliteDatabase();
    console.debug('Sqlite database removed');

    // Clean up local data
    await resetUser(true);
    await resetUserAuth(true);
    await handler.signOutSession();

    authLoading.set(false);

    viewer.update(($viewer) => {
      // Sign out user
      $viewer.activeType = 'home';
      return $viewer;
    });

    console.log('Account deleted successfully');
  } catch (error) {
    console.error('Failed to delete account:', error);
    throw error;
  }
};
